import { useCreateQrCodeNoteMutation } from '@/api/qr-codes/qrCodeMutations';
import {
  getAdminQrCodeNotesQuery,
  getDoctorQrCodesQuery,
} from '@/api/qr-codes/qrCodeQueries';
import type { QrCodeNote } from '@/api/qr-codes/qrCodeTypes';
import { currentUserQuery } from '@/api/user/userQueries';
import { Button } from '@/components/common/Button';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { Input } from '@/components/common/Input';
import { Pagination } from '@/components/common/Pagination';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { Spinner } from '@/components/common/Spinner';
import { Tooltip } from '@/components/common/Tooltip';
import { Card, CardsContainer } from '@/components/dashboard/DashboardCard';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const NOTES_PER_PAGE = 10;

export default function DoctorQrCodes() {
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedQr, setSelectedQr] = useState<QrCodeNote | null>(null);
  const [notesPage, setNotesPage] = useState(1);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newNote, setNewNote] = useState('');

  const { data: currentUser } = useQuery(currentUserQuery);
  const doctorId = currentUser?.data.id;

  const { data: doctorQrCodesData } = useQuery({
    ...getDoctorQrCodesQuery(doctorId!),
    enabled: !!doctorId,
  });

  const doctorQrCodes = doctorQrCodesData?.data ?? [];
  const hasAssignedQrCode = doctorQrCodes.length > 0;

  const { data: response, isLoading } = useQuery({
    ...getAdminQrCodeNotesQuery(notesPage, {
      doctor_id: doctorId,
      per_page: NOTES_PER_PAGE,
    }),
    enabled: !!doctorId,
  });

  const notes = response?.data ?? [];
  const notesMeta = response?.meta;

  const createNoteMutation = useMutation(useCreateQrCodeNoteMutation());

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date
      .toLocaleString('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      .replace(',', '');
  };

  const getQrCodeImageSrc = (base64: string) => {
    if (base64.startsWith('data:')) return base64;
    return `data:image/png;base64,${base64}`;
  };

  const handleCopyLink = (url: string) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast.success('Quiz link copied to clipboard');
  };

  const handleDisplayQrCode = (qr: QrCodeNote) => {
    setSelectedQr(qr);
    setQrDialogOpen(true);
  };

  const handleDownloadQrCode = (qr: QrCodeNote) => {
    const base64 = qr.qr_code_base64;
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([byteNumbers], { type: 'image/png' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `qr-code-${qr.id}.png`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCreateQrCode = () => {
    setNewNote('');
    setCreateDialogOpen(true);
  };

  const handleSubmitNote = () => {
    if (!newNote.trim() || !hasAssignedQrCode) return;
    createNoteMutation.mutate(
      { notes: newNote, parent_id: doctorQrCodes[0].id },
      {
        onSuccess: (createResponse) => {
          setCreateDialogOpen(false);
          setNotesPage(1);
          setSelectedQr(createResponse.data);
          setQrDialogOpen(true);
        },
      },
    );
  };

  return (
    <>
      <div className="space-y-4">
        <div>
          <h1 className="text-secondary text-4xl font-medium">Your QR codes</h1>
          <p className="text-table-foreground mt-2 text-base font-light">
            Leave a note to your pharmacist about the patient and their required
            supplements.
          </p>
        </div>

        <Tooltip
          content="You don't have an assigned QR code"
          enabled={!hasAssignedQrCode}
        >
          <span>
            <Button
              variant="outlinePrimary"
              size="md"
              animate
              onClick={handleCreateQrCode}
              className="mb-4 rounded-xl"
              disabled={!hasAssignedQrCode}
            >
              <Plus className="size-5" />
              <p>Create QR code</p>
            </Button>
          </span>
        </Tooltip>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="size-8" />
          </div>
        ) : notes.length === 0 ? (
          <p className="text-table-foreground text-base font-light">
            No QR notes yet. Create your first one above.
          </p>
        ) : (
          <CardsContainer className="rounded-3xl">
            <Card className="gap-0 divide-y px-6 py-0">
              {notes.map((qr) => (
                <div
                  key={qr.id}
                  className="flex items-center justify-between gap-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-table-foreground text-base font-medium">
                      {formatDate(qr.created_at)}
                    </p>
                    <p className="text-table-foreground mt-1 text-base font-light">
                      {qr.notes ?? '—'}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <RelationActionButton
                      onClick={() => handleDisplayQrCode(qr)}
                    >
                      Display QR code
                    </RelationActionButton>
                    <RelationActionButton
                      onClick={() => handleDownloadQrCode(qr)}
                    >
                      Download
                    </RelationActionButton>
                  </div>
                </div>
              ))}
              {notesMeta && notesMeta.last_page > 1 && (
                <div className="pt-4">
                  <Pagination
                    currentPage={notesMeta.current_page}
                    lastPage={notesMeta.last_page}
                    total={notesMeta.total}
                    perPage={notesMeta.per_page}
                    onPageChange={setNotesPage}
                    isLoading={isLoading}
                  />
                </div>
              )}
            </Card>
          </CardsContainer>
        )}
      </div>

      <DialogWrapper
        open={qrDialogOpen}
        onOpenChange={setQrDialogOpen}
        title="Your QR code"
        maxWidth="md"
      >
        {selectedQr && (
          <div className="flex flex-col items-center gap-4">
            {selectedQr.notes != null && (
              <p className="text-table-foreground text-center text-base font-light">
                {selectedQr.notes}
              </p>
            )}
            <img
              src={getQrCodeImageSrc(selectedQr.qr_code_base64)}
              alt="QR Code"
              className="size-64 object-contain"
            />
            <RelationActionButton
              onClick={() => handleCopyLink(selectedQr.url)}
            >
              Copy link
            </RelationActionButton>
          </div>
        )}
      </DialogWrapper>

      <DialogWrapper
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        title="Create note"
        maxWidth="lg"
      >
        <div className="flex flex-col gap-6 pt-3">
          <Input
            label="Enter note"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmitNote}
            disabled={!newNote.trim() || createNoteMutation.isPending}
            className="self-start"
          >
            {createNoteMutation.isPending ? 'Creating...' : 'Add note'}
          </Button>
        </div>
      </DialogWrapper>
    </>
  );
}
